// Pipeline (admin) — humanised, Curro-redesigned view of the data pipeline.
// Five-step flow strip, source cards, run-history timeline with period
// coverage + overlap chips, a Quality-report modal (pass-rate bar + checklist
// cards + % empty column bars) and a "Look inside" drill modal.
// Data wiring (PipelineApi / RunsApi) is unchanged from the previous version.

import { useEffect, useRef, useState } from "react";
import { PipelineApi, RunsApi } from "../hooks/useApi.js";
import SourceWizard from "../components/SourceWizard.jsx";
import Icon from "../components/Icon.jsx";
import { PassGauge, ReconDeviation, ToleranceGauges, ColumnHealthBars } from "../components/Charts.jsx";
import { fmtR, fmtRm, fmtN, PALETTE } from "../lib/format.js";

const TRANSPORT_LABEL = { "xlsx-drop": "Excel file", "csv-drop": "CSV file", "http-api": "Live API pull", "ftp": "FTP pull" };
const TRANSPORT_ICON = { "xlsx-drop": "file", "csv-drop": "table", "http-api": "api", "ftp": "globe" };
const STEP_LABELS = ["Received", "Standardised", "Calculated", "Quality checks", "Published"];

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" });
};
const parseJson = (s, fallback) => { try { return JSON.parse(s || "") ?? fallback; } catch { return fallback; } };

/** The five pipeline stages as a mini progress strip. */
function StepStrip({ states }) {
  return (
    <div className="steps">
      {STEP_LABELS.map((l, i) => (
        <span key={l} style={{ display: "contents" }}>
          <div className={`st ${states[i]}`}>
            <div className="b">{states[i] === "done" ? <Icon name="check" size={10} /> : states[i] === "fail" ? <Icon name="x" size={10} /> : i + 1}</div>
            {l}
          </div>
          {i < STEP_LABELS.length - 1 && <div className={`conn ${states[i] === "done" ? "done" : ""}`} />}
        </span>
      ))}
    </div>
  );
}

/* Classify a check for charting. The backend sets `kind` (recon | tolerance |
   boolean); older reports predate it, so fall back to boolean — they degrade
   to the status-dot list rather than the richer recon/tolerance charts. */
const kindOf = (c) => c.kind ?? "boolean";

/* ---------- Quality Report modal ---------- */
function QualityReportModal({ qr, onClose }) {
  const clean = qr.verdict === "clean";
  const passed = qr.checks.filter((c) => c.pass).length;
  const total = qr.checks.length;
  const booleanChecks = qr.checks.filter((c) => kindOf(c) === "boolean");
  const reconChecks = qr.checks.filter((c) => kindOf(c) === "recon");
  const toleranceChecks = qr.checks.filter((c) => kindOf(c) === "tolerance");
  const stats = [
    { v: fmtN(qr.factRows), l: "Rows" },
    { v: fmtRm(qr.netSpend), l: "Net spend" },
    { v: String(qr.entities), l: "Campuses" },
    { v: fmtN(qr.vendors), l: "Vendors" },
    { v: `${qr.periodMin ?? "—"} → ${qr.periodMax ?? "—"}`, l: "Period covered", wide: true },
  ];
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="mh-ic" style={{ background: clean ? "linear-gradient(135deg,var(--curro-green),#15b06a)" : "linear-gradient(135deg,var(--neg),#d23b2e)" }}>
            <Icon name="shield" size={21} />
          </div>
          <div>
            <h2>Quality report · #{qr.runId}</h2>
            <div className="sub">{qr.sourceKey} · validation harness · {fmtWhen(qr.generatedAtUtc)}</div>
          </div>
          <button type="button" className="x" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>

        <div className="modal-body">
          <div className={`test-verdict ${clean ? "clean" : "blocked"}`} style={{ marginTop: 14 }}>
            <div className="tv-ic"><Icon name={clean ? "check" : "alert"} size={28} /></div>
            <div className="tv-txt">
              <b>{clean ? "Passed — auto-published to live" : "Blocked — never shown to users"}</b>
              <span>{passed}/{qr.checks.length} checks passed{clean ? "" : " · kept here for diagnosis"}</span>
            </div>
            <button type="button" className="btn ghost sm" style={{ marginLeft: "auto" }} onClick={() => window.print()}>
              <Icon name="download" size={14} /> Print
            </button>
          </div>

          <div className="qr-grid">
            {stats.map((s) => (
              <div className="qr-stat" key={s.l} style={s.wide ? { gridColumn: "span 2" } : undefined}>
                <div className="v num">{s.v}</div><div className="l">{s.l}</div>
              </div>
            ))}
          </div>

          {qr.overlappingRuns?.length > 0 && (
            <div className="qr-warn">
              <Icon name="alert" size={16} />
              <div><b>Timeframe overlaps other published snapshots.</b>{" "}
                {qr.overlappingRuns.map((o) => `#${o.runId} (${o.sourceKey}, ${o.periodMin}→${o.periodMax})`).join(" · ")}
                {" "}— possible resend or correction. Confirm which snapshot is authoritative for the overlapping months.</div>
            </div>
          )}

          <div className="eyebrow" style={{ marginTop: 18 }}>Quality checks</div>
          <div className="qr-checks-viz">
            <PassGauge passed={passed} total={total} />
            <div className="qr-bool-list">
              {booleanChecks.map((c, i) => (
                <div key={i} className={`qr-bool ${c.pass ? "ok" : "fail"}`}>
                  <span className="qb-ic"><Icon name={c.pass ? "check" : "x"} size={12} /></span>
                  <span className="qb-nm">{c.name}</span>
                  <span className="qb-vl num">{c.actual}</span>
                </div>
              ))}
            </div>
          </div>

          {reconChecks.length > 0 && (
            <>
              <div className="eyebrow" style={{ marginTop: 22 }}>Reconciliation <span className="hint" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>· deviation from expected (on the line = match)</span></div>
              <ReconDeviation items={reconChecks} />
            </>
          )}

          {toleranceChecks.length > 0 && (
            <>
              <div className="eyebrow" style={{ marginTop: 22 }}>Tolerances <span className="hint" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>· actual vs allowed ceiling (line)</span></div>
              <ToleranceGauges items={toleranceChecks} />
            </>
          )}

          <div className="eyebrow" style={{ marginTop: 22 }}>Column health <span className="hint" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>· % empty (red = needs attention)</span></div>
          <ColumnHealthBars columns={qr.columnHealth} />

          {qr.unmappedColumns?.length > 0 && (
            <p className="hint" style={{ marginTop: 14 }}>
              <Icon name="info" size={13} /> {qr.unmappedColumns.length} source columns unmapped (ignored):{" "}
              <span className="num">{qr.unmappedColumns.slice(0, 10).join(", ")}{qr.unmappedColumns.length > 10 ? "…" : ""}</span>
            </p>
          )}
          <p className="hint" style={{ marginTop: 6, color: "var(--muted-2)" }}>
            Stored in the database (canonical) · a portable <span className="num">quality_report.json</span> copy lives in the run folder.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------- "Look inside" drill modal (standardised vs final) ---------- */
function DrillModal({ runId, onClose }) {
  const [table, setTable] = useState("fact");
  const [preview, setPreview] = useState(null);
  const [columns, setColumns] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPreview(null); setColumns(null); setError(null);   // eslint-disable-line react-hooks/set-state-in-effect
    Promise.all([PipelineApi.preview(runId, table, 25), PipelineApi.columns(runId, table)])
      .then(([p, c]) => { setPreview(p); setColumns(c); })
      .catch((e) => setError(e.body?.error ?? e.message));
  }, [runId, table]);

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="mh-ic"><Icon name="database" size={20} /></div>
          <div>
            <h2>Inside snapshot #{runId}</h2>
            <div className="sub">Column health &amp; a sample of the {table === "staged" ? "standardised" : "final"} data</div>
          </div>
          <button type="button" className="x" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="seg sm" style={{ marginTop: 14, alignSelf: "flex-start", display: "inline-flex" }}>
            <button type="button" className={table === "staged" ? "on" : ""} onClick={() => setTable("staged")}><Icon name="wand" size={14} /> Standardised</button>
            <button type="button" className={table === "fact" ? "on" : ""} onClick={() => setTable("fact")}><Icon name="spark" size={14} /> Final</button>
          </div>
          <p className="hint" style={{ marginTop: 8 }}>Standardised = after column mapping · Final = after calculations (what the report runs on).</p>

          {error && <div className="error"><Icon name="alert" size={15} /> {error}</div>}
          {!error && !columns && <p className="hint" style={{ display: "flex", alignItems: "center", gap: 8 }}><span className="spinner" style={{ color: "var(--primary)" }} /> Reading the snapshot…</p>}

          {columns && (
            <>
              <div className="eyebrow" style={{ marginTop: 14 }}>Column health <span className="hint" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>· % empty (red = often explains a block)</span></div>
              <ColumnHealthBars columns={columns.columns} />
            </>
          )}
          {preview && (
            <>
              <div className="eyebrow" style={{ marginTop: 18 }}>First {preview.rows.length} rows (∅ = empty)</div>
              <div className="scroll-x" style={{ marginTop: 8 }}>
                <table className="dense">
                  <thead><tr>{preview.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                  <tbody>
                    {preview.rows.map((row, i) => (
                      <tr key={i}>{row.map((v, j) => <td key={j} className={v === null ? "null-cell" : ""}>{v ?? "∅"}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- "Process now" modal — runs the REAL ingest ---------- */
function ProcessModal({ source, onClose, onDone }) {
  const [stage, setStage] = useState(0);
  const [outcome, setOutcome] = useState(null);
  const [error, setError] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;   // StrictMode double-effect guard — one ingest only
    startedRef.current = true;
    const timers = [500, 1200, 1900, 2600].map((t, i) => setTimeout(() => setStage((s) => Math.max(s, i + 1)), t));
    PipelineApi.ingest(source.sourceKey)
      .then((o) => { setOutcome(o); onDone?.(); })
      .catch((e) => setError(e.body?.error ?? e.message))
      .finally(() => timers.forEach(clearTimeout));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const settled = outcome != null || error != null;
  const states = STEP_LABELS.map((_, i) => {
    if (!settled) return stage > i ? "done" : stage === i ? "pending" : "";
    if (error || outcome?.status === "failed") return i === 0 ? "done" : i === 1 ? "fail" : "skip";
    if (outcome?.status === "skipped_duplicate") return i === 0 ? "done" : "skip";
    if (outcome?.verdict === "poisoned") return i < 3 ? "done" : i === 3 ? "fail" : "skip";
    return "done";
  });

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && settled && onClose()}>
      <div className="modal" style={{ width: "min(640px,100%)" }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="mh-ic"><Icon name="bolt" size={20} /></div>
          <div><h2>Processing {source.displayName}</h2><div className="sub">Running all five pipeline stages</div></div>
          {settled && <button type="button" className="x" onClick={onClose}><Icon name="x" size={18} /></button>}
        </div>
        <div className="modal-body" style={{ paddingTop: 22, paddingBottom: 26 }}>
          <StepStrip states={states} />

          {!settled && (
            <p className="hint" style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 8 }}>
              <span className="spinner" style={{ color: "var(--primary)" }} /> Working through the stages…
            </p>
          )}

          {error && <div className="error" style={{ marginTop: 18 }}><Icon name="alert" size={15} /> {error}</div>}

          {outcome?.status === "skipped_duplicate" && (
            <div className="notice" style={{ marginTop: 18 }}>
              <Icon name="info" size={15} /> That exact file has already been processed — nothing new to do (idempotent no-op).
            </div>
          )}

          {outcome?.status === "failed" && (
            <div className="error" style={{ marginTop: 18 }}><Icon name="alert" size={15} /> {outcome.error}</div>
          )}

          {outcome?.verdict === "clean" && (
            <div className="test-verdict clean" style={{ marginTop: 20 }}>
              <div className="tv-ic"><Icon name="check" size={28} /></div>
              <div className="tv-txt">
                <b>Snapshot #{outcome.publishRunId} is live</b>
                <span>Passed every quality check · {fmtN(outcome.stagedRows)} rows processed.</span>
              </div>
            </div>
          )}

          {outcome?.verdict === "poisoned" && (
            <>
              <div className="test-verdict blocked" style={{ marginTop: 20 }}>
                <div className="tv-ic"><Icon name="alert" size={28} /></div>
                <div className="tv-txt">
                  <b>Snapshot #{outcome.publishRunId} was blocked</b>
                  <span>Never shown to users — kept below for diagnosis (quality report &amp; look-inside).</span>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(outcome.reasons ?? []).map((r, i) => (
                  <div key={i} className="error" style={{ margin: 0, padding: "9px 12px" }}><Icon name="alert" size={14} /> {r}</div>
                ))}
              </div>
            </>
          )}

          {settled && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <button type="button" className="btn primary" onClick={onClose}><Icon name="check" size={15} /> Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Pipeline() {
  const [sources, setSources] = useState([]);
  const [ingestRuns, setIngestRuns] = useState([]);
  const [publishRuns, setPublishRuns] = useState([]);
  const [liveRunId, setLiveRunId] = useState(null);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [wizard, setWizard] = useState(null);       // null | "new" | a source object
  const [processing, setProcessing] = useState(null); // source being ingested
  const [drill, setDrill] = useState(null);           // runId
  const [qr, setQr] = useState(null);                 // loaded quality report

  const load = () => {
    PipelineApi.sources().then(setSources).catch((e) => setError(e.message));
    PipelineApi.ingestRuns().then(setIngestRuns).catch(() => {});
    RunsApi.list().then(setPublishRuns).catch(() => {});
    RunsApi.current().then((r) => setLiveRunId(r?.id ?? null)).catch(() => setLiveRunId(null));
  };
  useEffect(() => { load(); }, []);

  const openQr = async (runId) => {
    setQr(null); setError(null);
    try { setQr(await PipelineApi.qualityReport(runId)); }
    catch (e) { setError(e.body?.error ?? e.message); }
  };

  // Period helpers: coverage label + overlap detection (resend/correction signal).
  const periodOf = (pr) => (pr?.periodMin && pr?.periodMax ? `${pr.periodMin} → ${pr.periodMax}` : null);
  const overlapsOf = (pr) =>
    pr?.periodMin && pr?.periodMax
      ? publishRuns.filter((o) =>
          o.id !== pr.id && o.verdict === "clean" && o.periodMin && o.periodMax &&
          pr.periodMin <= o.periodMax && o.periodMin <= pr.periodMax)
      : [];

  // Merge ingest + publish into one human story, newest first.
  const history = ingestRuns.map((ir) => ({ ir, pr: publishRuns.find((p) => p.ingestRunId === ir.id) ?? null }));

  const storyOf = ({ ir, pr }) => {
    if (ir.status === "failed")
      return { dot: "bad", chip: "bad", title: "Couldn't process the file", detail: ir.error, steps: ["done", "fail", "skip", "skip", "skip"] };
    if (ir.status === "skipped_duplicate")
      return { dot: "muted", chip: "muted", title: "Skipped — identical file already processed", detail: "Re-sending the same file never creates a duplicate snapshot.", steps: ["done", "skip", "skip", "skip", "skip"] };
    if (ir.status === "running")
      return { dot: "warn", chip: "warn", title: "Processing…", detail: null, steps: ["done", "pending", "pending", "pending", "pending"] };
    if (pr?.verdict === "clean") {
      const live = pr.id === liveRunId;
      return {
        dot: "ok", chip: "ok",
        title: `Published as snapshot #${pr.id}${live ? " — LIVE now" : ""}`,
        detail: `${fmtN(pr.rowCount)} rows · ${fmtR(pr.netSpendTotal)} net spend. ${live ? "This is what everyone sees right now." : "Superseded by a newer snapshot."}`,
        steps: ["done", "done", "done", "done", "done"],
      };
    }
    if (pr?.verdict === "poisoned") {
      return {
        dot: "bad", chip: "bad",
        title: `Blocked by quality checks (snapshot #${pr.id})`,
        detail: "Never shown to users — kept here so you can see what went wrong.",
        reasons: parseJson(pr.reasonsJson, []),
        steps: ["done", "done", "done", "fail", "skip"],
      };
    }
    return { dot: "muted", chip: "muted", title: ir.status, detail: null, steps: ["done", "pending", "pending", "pending", "pending"] };
  };

  const lastOutcomeFor = (sourceKey) => {
    const h = history.find((x) => x.ir.sourceKey === sourceKey);
    return h ? storyOf(h) : null;
  };
  const liveSourceKey = publishRuns.find((p) => p.id === liveRunId)?.sourceKey ?? null;

  return (
    <div className="rise">
      <div className="page-head">
        <div>
          <div className="eyebrow">Admin · Pipeline</div>
          <h1>Data pipeline</h1>
          <p className="lead">Every source runs the same five steps. If a quality check fails, the snapshot is blocked —
            users keep the last good one, and the blocked data stays here for you to inspect.</p>
        </div>
        <button type="button" className="btn primary lg" onClick={() => setWizard("new")}><Icon name="plus" size={17} /> Add a source</button>
      </div>

      {/* how it works */}
      <div className="panel">
        <div className="flow">
          {[
            ["Receive", "a file or API arrives", "download"],
            ["Standardise", "columns map to our format", "wand"],
            ["Calculate", "the figures are computed", "spark"],
            ["Quality checks", "totals & rules must pass", "shield"],
            ["Publish", "a numbered snapshot goes live", "bolt"],
          ].map(([t, s, ic], i) => (
            <div className="flow-step" key={t}>
              {i > 0 && <span className="flow-arrow"><Icon name="chevR" size={18} /></span>}
              <div className="flow-card">
                <div className="n" style={{ background: PALETTE[i] }}><Icon name={ic} size={15} /></div>
                <div><b>{t}</b><small>{s}</small></div>
              </div>
            </div>
          ))}
        </div>
        <p className="hint" style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          New to the jargon?
          <button type="button" className="btn ghost sm" onClick={() => setShowHelp(!showHelp)}>{showHelp ? "Hide" : "What do these words mean?"}</button>
        </p>
        {showHelp && (
          <div className="rise" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12, marginTop: 6 }}>
            {[
              ["Snapshot", "One processed version of the data. Numbered (#1, #2…) and never changes once made."],
              ["Live", "The snapshot everyone currently sees in Report & Explore."],
              ["Blocked", "Failed a quality rule (e.g. too many empty campus codes). Kept for diagnosis, never shown."],
              ["Standardised vs final", "Standardised = after column mapping. Final = after calculations — what the report runs on. Tip: pin any snapshot in the top-right picker to preview its Report & A/B output."],
            ].map(([t, d]) => (
              <div key={t} className="card pad" style={{ padding: 14 }}>
                <b style={{ fontFamily: "var(--font-head)", color: "var(--ink)", display: "block", marginBottom: 4 }}>{t}</b>
                <span className="hint">{d}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div className="error"><Icon name="alert" size={16} /> {error}</div>}

      {/* sources */}
      <h2 className="section-title">Where the data comes from <span className="count">{sources.length}</span></h2>
      <div className="sources-grid">
        {sources.map((s) => {
          const last = lastOutcomeFor(s.sourceKey);
          const live = s.sourceKey === liveSourceKey;
          return (
            <div className="source-card" key={s.sourceKey}>
              <div className="topline" style={{ background: live ? "linear-gradient(90deg,var(--curro-green),var(--curro-sky))" : "linear-gradient(90deg,var(--curro-blue),var(--curro-sky))" }} />
              <div className="sc-head">
                <div className="sc-ic"><Icon name={TRANSPORT_ICON[s.transportKey] ?? "database"} size={21} /></div>
                <div style={{ flex: 1 }}>
                  <b>{s.displayName}</b>
                  <span className="key num">{s.sourceKey}</span>
                </div>
                {live && <span className="chip ok"><Icon name="bolt" size={12} /> Live</span>}
              </div>
              <div className="sc-meta">
                <div className="row"><Icon name={TRANSPORT_ICON[s.transportKey] ?? "database"} size={14} /> {TRANSPORT_LABEL[s.transportKey] ?? s.transportKey}</div>
                <div className="row"><Icon name="clock" size={14} /> {s.scheduleUtc ? `Auto-fetch daily at ${s.scheduleUtc} UTC` : "Manual — process on demand"}</div>
                <div className="row"><Icon name="wand" size={14} /> Mapping profile <span className="num">&nbsp;{s.profileKey}</span></div>
              </div>
              {last && <div className={`chip ${last.chip}`}>{last.title}</div>}
              <div className="sc-actions">
                <button type="button" className="btn primary" disabled={!s.enabled || processing !== null}
                  onClick={() => setProcessing(s)}>
                  <Icon name="play" size={15} /> Process now
                </button>
                <button type="button" className="btn ghost" onClick={() => setWizard(s)}><Icon name="settings" size={15} /> Set up / test</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* history */}
      <h2 className="section-title">What's happened so far <span className="count">{history.length}</span></h2>
      <div className="timeline">
        {history.map(({ ir, pr }) => {
          const story = storyOf({ ir, pr });
          return (
            <div className="tl-item" key={ir.id}>
              <div className={`dot ${story.dot}`}>
                {story.dot === "ok" ? <Icon name="check" size={11} /> : story.dot === "bad" ? <Icon name="x" size={11} /> : <Icon name="clock" size={11} />}
              </div>
              <div className="hist-card">
                <div className="hist-head">
                  <span className={`chip ${story.chip}`} style={{ fontSize: 12.5 }}>{story.title}</span>
                  <span className="hist-when"><Icon name="file" size={13} /> {ir.sourceKey} · {fmtWhen(ir.startedAt)}</span>
                </div>
                <StepStrip states={story.steps} />
                {story.detail && <div className="hist-detail">{story.detail}</div>}
                {pr && periodOf(pr) && (
                  <div className="hist-detail" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span className="chip muted"><Icon name="calendar" size={12} /> Covers {periodOf(pr)}</span>
                    {overlapsOf(pr).length > 0 && (
                      <span className="chip warn"><Icon name="alert" size={12} /> Overlaps {overlapsOf(pr).map((o) => `#${o.id} (${o.sourceKey})`).join(", ")} — possible resend</span>
                    )}
                  </div>
                )}
                {story.reasons?.length > 0 && (
                  <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                    {story.reasons.map((r, i) => <div key={i} className="error" style={{ margin: 0, padding: "9px 12px" }}><Icon name="alert" size={14} /> {r}</div>)}
                  </div>
                )}
                {pr?.runPath && (
                  <div className="sc-actions" style={{ marginTop: 12 }}>
                    <button type="button" className="btn soft sm" onClick={() => openQr(pr.id)}><Icon name="shield" size={14} /> Quality report</button>
                    <button type="button" className="btn ghost sm" onClick={() => setDrill(pr.id)}><Icon name="eye" size={14} /> Look inside</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {wizard && (
        <SourceWizard
          existing={wizard === "new" ? null : wizard}
          onClose={() => setWizard(null)}
          onChanged={load}
        />
      )}
      {processing && <ProcessModal source={processing} onClose={() => setProcessing(null)} onDone={load} />}
      {drill && <DrillModal runId={drill} onClose={() => setDrill(null)} />}
      {qr && <QualityReportModal qr={qr} onClose={() => setQr(null)} />}
    </div>
  );
}
